import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getSearchSchema, searchAutocomplete, SearchFieldDto } from 'services/searchAdvanced';
import { FieldDescriptor } from 'types/advancedSearch';

const MIN_AUTOCOMPLETE_LENGTH = 2;

// Map one API SearchField → the bar's FieldDescriptor. `async` fields get an
// `asyncSearch` bound to the generic autocomplete endpoint (debounced by the bar).
function toDescriptor(model: string, field: SearchFieldDto): FieldDescriptor {
  const base = { label: field.label, wireKey: field.field };

  switch (field.kind) {
    case 'boolean':
      return { ...base, kind: 'boolean' };
    case 'date':
      return { ...base, kind: 'date' };
    case 'enum':
      return { ...base, kind: 'enum', multiple: field.multiple, options: field.options ?? [] };
    case 'async':
      return {
        ...base,
        kind: 'async',
        asyncMultiple: field.multiple,
        emptyOption: field.emptyValue ? { value: field.emptyValue, label: 'None' } : undefined,
        asyncSearch: async (query: string) => {
          if (!query || query.length < MIN_AUTOCOMPLETE_LENGTH) return [];
          const res = await searchAutocomplete(model, field.field, query);
          return (res.data?.values ?? []).map(value => ({ value, label: value }));
        },
      };
    default:
      return { ...base, kind: 'text' };
  }
}

/** Fetch a model's searchable fields and adapt them to AdvancedSearchBar descriptors. */
export function useSearchSchema(model: string, enabled = true) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['advanced-search-schema', model],
    queryFn: () => getSearchSchema(model),
    enabled: enabled && !!model,
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
  });

  const descriptors = useMemo<FieldDescriptor[]>(
    () => (data?.data?.fields ?? []).map(field => toDescriptor(model, field)),
    [data, model],
  );

  return { descriptors, isLoading, isError };
}
